// The rules of a time window and a project's Timeline (spec §6.8), pure, so
// they are tested here and the screens stay layout. Shared by the studio
// Schedule (GanttScreen) and a project's Timeline tab.
//
// Dates are 'YYYY-MM-DD' keys in the org's calendar; a span's end is the last
// day it covers, so a one-day item is { start: d, end: d }.
import { addDaysToKey } from './dates';
import { byRank, type Issue } from './issues';

export type Unit = 'days' | 'weeks';
/** The design's window choices: 7–60 days, or 4–20 weeks. */
export const RANGES: Record<Unit, readonly number[]> = {
  days: [7, 14, 30, 60],
  weeks: [4, 8, 12, 20],
};

export type TimeWindow = { from: string; unit: Unit; cols: number };
export type Span = { start: string; end: string };

/** Whole days from one key to another; both parse as UTC midnight. */
export const daysBetween = (a: string, b: string): number =>
  Math.round((Date.parse(b) - Date.parse(a)) / 86_400_000);

export const windowDays = (w: TimeWindow): number => w.cols * (w.unit === 'days' ? 1 : 7);
export const windowEnd = (w: TimeWindow): string => addDaysToKey(w.from, windowDays(w) - 1);
/** The first day of each column. */
export const columnKeys = (w: TimeWindow): string[] =>
  Array.from({ length: w.cols }, (_, i) => addDaysToKey(w.from, i * (w.unit === 'days' ? 1 : 7)));

/** A day's left edge as a fraction of the window (below 0 or past 1 when outside it). */
export const xOf = (key: string, w: TimeWindow): number => daysBetween(w.from, key) / windowDays(w);

/** A span across the window as [from, to) fractions, clamped; null when wholly outside. */
export const place = (s: Span, w: TimeWindow): { from: number; to: number } | null => {
  const l = xOf(s.start, w);
  const r = xOf(addDaysToKey(s.end, 1), w);
  if (r <= 0 || l >= 1) return null;
  return { from: Math.max(0, l), to: Math.min(1, r) };
};

/** The window moved by half its width, whole columns at a time. */
export const pan = (w: TimeWindow, dir: -1 | 1): TimeWindow => ({
  ...w,
  from: addDaysToKey(
    w.from,
    dir * Math.max(1, Math.floor(w.cols / 2)) * (w.unit === 'days' ? 1 : 7),
  ),
});

/**
 * A work item's bar. Start to due; with only one of the two it is one day long
 * (Jira's fallback); with neither there is no bar. A due date before the start
 * draws on the start day alone rather than backwards.
 */
export const spanOf = (i: Pick<Issue, 'start_date' | 'due_date'>): Span | null => {
  const start = i.start_date ?? i.due_date;
  const end = i.due_date ?? i.start_date;
  if (!start || !end) return null;
  return end < start ? { start, end: start } : { start, end };
};

/**
 * An epic's bar from its children (spec §6.8): the earliest start to the latest
 * end of those with dates, and progress as the share of all its children done.
 * Null when no child has a date.
 */
export const rollUp = (
  kids: { span: Span | null; done: boolean }[],
): { span: Span; progress: number } | null => {
  const dated = kids.flatMap((k) => (k.span ? [k.span] : []));
  if (!dated.length) return null;
  const start = dated.map((s) => s.start).sort()[0];
  const end = dated
    .map((s) => s.end)
    .sort()
    .at(-1)!;
  return { span: { start, end }, progress: kids.filter((k) => k.done).length / kids.length };
};

/** A blocks link is wrong when the blocked item starts before its blocker has ended. */
export const clash = (blocker: Span, blocked: Span): boolean => blocked.start <= blocker.end;

/**
 * A link drawn between two bars, as Jira sets it: whichever is scheduled first
 * (earlier start, then earlier end) blocks the other; a tie keeps the order it
 * was drawn in. Returns [blocker, blocked].
 */
export const blockerFirst = <T extends { span: Span | null }>(from: T, to: T): [T, T] => {
  const a = from.span;
  const b = to.span;
  if (!a || !b) return [from, to];
  const later = b.start < a.start || (b.start === a.start && b.end < a.end);
  return later ? [to, from] : [from, to];
};

/** How a bar was dragged: the whole bar, or one of its ends. */
export type Grip = 'move' | 'start' | 'end';

/** A span after a drag of `days`. An end never passes the other: a bar is at least a day. */
export const dragSpan = (s: Span, grip: Grip, days: number): Span => {
  if (grip === 'move')
    return { start: addDaysToKey(s.start, days), end: addDaysToKey(s.end, days) };
  if (grip === 'start') {
    const start = addDaysToKey(s.start, days);
    return { start: start > s.end ? s.end : start, end: s.end };
  }
  const end = addDaysToKey(s.end, days);
  return { start: s.start, end: end < s.start ? s.start : end };
};

/**
 * A focused bar's keys (spec §6.8): ← → move it a day, Shift+← → move its end.
 * Null for any other key, so the caller lets it through.
 */
export const nudge = (s: Span, key: string, shift: boolean): Span | null => {
  if (key !== 'ArrowLeft' && key !== 'ArrowRight') return null;
  return dragSpan(s, shift ? 'end' : 'move', key === 'ArrowRight' ? 1 : -1);
};

// ── Rows ─────────────────────────────────────────────────────────────────────

/** Where a work item hangs: a subtask under its parent, a story/task/bug under its epic. */
export const parentOf = (i: Pick<Issue, 'issue_type' | 'parent_id' | 'epic_id'>): string | null =>
  i.issue_type === 'subtask' ? i.parent_id : i.issue_type === 'epic' ? null : i.epic_id;

export type TimelineRow = {
  issue: Issue;
  depth: number;
  kids: number;
  /** The row it hangs under here (null at the top level): its siblings share it. */
  parent: string | null;
};

/**
 * The left column in Jira's hierarchy: each item in rank order, its children
 * beneath it unless it is collapsed. An item whose parent isn't shown (filtered
 * out) stands at the top level.
 */
export const timelineRows = (shown: Issue[], collapsed: ReadonlySet<string>): TimelineRow[] => {
  const ids = new Set(shown.map((i) => i.id));
  const kids = new Map<string | null, Issue[]>();
  for (const i of [...shown].sort(byRank)) {
    const p = parentOf(i);
    const at = p && ids.has(p) ? p : null;
    kids.set(at, [...(kids.get(at) ?? []), i]);
  }
  const out: TimelineRow[] = [];
  const walk = (parent: string | null, depth: number) => {
    for (const issue of kids.get(parent) ?? []) {
      const n = kids.get(issue.id)?.length ?? 0;
      out.push({ issue, depth, kids: n, parent });
      if (n && !collapsed.has(issue.id)) walk(issue.id, depth + 1);
    }
  };
  walk(null, 0);
  return out;
};

/**
 * The ranks to place `moved` between when it lands before (`top`) or after
 * (`bottom`) `target` among its siblings, for rank_between(lo, hi). Ranks are
 * kept per board column, so two siblings in different columns can share one;
 * with no room between a tie it goes after the tied group.
 * ponytail: off by the size of a tie; a global rank rebalance removes ties.
 */
export const reorderBounds = (
  siblings: Pick<Issue, 'id' | 'rank'>[],
  movedId: string,
  targetId: string,
  edge: 'top' | 'bottom',
): { lo: string; hi: string | null } | null => {
  const list = [...siblings].filter((s) => s.id !== movedId).sort(byRank);
  const t = list.findIndex((s) => s.id === targetId);
  if (t < 0 || movedId === targetId) return null;
  const at = edge === 'top' ? t : t + 1;
  const lo = list[at - 1]?.rank ?? '';
  const hi = list.slice(at).find((s) => s.rank > lo)?.rank ?? null;
  return { lo, hi };
};
